import { useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Table,
  styled,
  TableCell,
  TableBody,
  Grid,
} from "@mui/material";
import { SECTOR_PERFORMANCE } from "../_constants/sector_strategy";
import { SectorPerformance } from "../_types/sector_strategy.type";

// Optional: if you already have a Formatters helper, swap these with it.
const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const fmtPct = (n: number) =>
  `${(n * 100).toFixed(2)}%`;

const monthName = (period: string) => {
  // period is "YYYY-MM"
  const [y, m] = period.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString("en-US", { month: "long" });
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function SectorPerformanceTable() {
  // Group by year and keep months sorted
  const dataByYear = useMemo(() => {
    const map = new Map<string, SectorPerformance[]>();
    const sorted = [...SECTOR_PERFORMANCE].sort((a, b) => a.period.localeCompare(b.period));
    for (const r of sorted) {
      const year = r.period.slice(0, 4);
      if (!map.has(year)) map.set(year, []);
      map.get(year)!.push(r);
    }
    return map;
  }, []);

  return (
    <Container sx={{ display: "flex", flexDirection: "column", pb: 4 }}>
      <Grid container>
        <Grid item xs={12} lg={10}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h4">Sector Rotation Strategy — Since Inception</Typography>
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Monthly performance split by calendar year
          </Typography>
        </Grid>
      </Grid>

      {Array.from(dataByYear.keys()).reverse().map((year) => {
        const rows = dataByYear.get(year)!;
        return (
          <Box key={year} sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {year}
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 800, overflow: "auto" }}>
              <Table aria-label={`sector performance ${year}`}>
                <TableHead
                  sx={{
                    "& th": {
                      backgroundColor: "primary.main",
                      color: "primary.contrastText",
                      position: "sticky",
                      top: 0,
                      zIndex: 20,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">Month Start Value</TableCell>
                    <TableCell align="right">Month End Value</TableCell>
                    <TableCell align="right">Gain/Loss — Month</TableCell>
                    <TableCell align="right">%Gain/Loss — MoM</TableCell>
                    <TableCell align="right">%Gain/Loss — Cumm</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <StyledTableRow key={r.period}>
                      <TableCell component="th" scope="row">
                        {monthName(r.period)}
                      </TableCell>
                      <TableCell align="right">{fmtMoney(r.start_value)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.end_value)}</TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: r.pnl_abs >= 0 ? "success.main" : "error.main", fontWeight: 600 }}
                      >
                        {fmtMoney(r.pnl_abs)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: r.pct_mom >= 0 ? "success.main" : "error.main", fontWeight: 600 }}
                      >
                        {fmtPct(r.pct_mom)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: r.pct_cum >= 0 ? "success.main" : "error.main", fontWeight: 600 }}
                      >
                        {fmtPct(r.pct_cum)}
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Container>
  );
}
